<template>
  <BaseModal
    no-padding
    no-exit
    card-width="520px"
  >
    <template #toolbar>
      <div class="w-full flex items-start gap-3 pb-5">
        <TargetGoalIcon class="text-icon-normal" />
        <div class="space-y-2">
          <BaseText type="label" size="sm" class="text-text-muted">
            Creative Test Targets
          </BaseText>
          <BaseText size="sm" class="text-text-normal">
            Customize definitions for your creative testing dashboard.
          </BaseText>
        </div>
      </div>
    </template>
    <template #default>
      <div class="border-t border-border-normal w-full" />
      <div v-if="hasMounted" class="w-full h-full p-5">
        <div v-show="isTargetValid.message" class="flex items-center gap-1 mb-4">
          <AltAlertIcon class="text-secondary-red-100" />
          <BaseText type="body" size="xs" class="text-secondary-red-100">
            {{ isTargetValid.message }}
          </BaseText>
        </div>
        
        <!-- New Launched -->
        <div class="flex items-center gap-1">
          <NewLaunchedIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            New Launched
          </BaseText>
        </div>
        <div class="mt-2">
          <div class="border text-text-muted border-border-normal rounded-md w-full flex items-center">
            <div class="px-2.5 py-1 border-r border-border-normal">
              <BaseText type="label" size="sm">
                Launched
              </BaseText>
            </div>
            <BaseText type="label" size="sm" class="px-2.5 py-1">
              {{ dateFilterLabel ?? 'N/A' }}
            </BaseText>
          </div>
        </div>
        <div class="border-t border-border-normal w-full my-5" />
        <!-- Platform Toggle -->
        <PlatformToggle 
          v-if="showPlatformToggle" 
          :current-platform="platformToggle" 
          @select="platformToggle = $event"
          class="mb-4"
          use-parent-width
        />
        <!-- Scaling -->
        <div class="flex items-center gap-1">
          <ScaleIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Scaling
          </BaseText>
        </div>
        <div class="mt-2">
          <QueryCondition
            :key="`scaling-${platformToggle}`"
            class="border-none fade-in"
            style="padding: 0px !important"
            hide-clause
            hide-actions
            :filter-condition="creativeTargetsFilters[platformToggle]?.scaling ?? {}"
            filter-option-type="metric"
            :freeze-property="true"
            :freeze-operation="true"
            :currency-region="currencyRegion"
            @selectProperty="changeConditionProperty('scaling', $event)"
            @selectOperation="changeConditionOperation('scaling', $event)"
            @updateConditionValue="updateConditionValue('scaling', $event)"
          />
        </div>
        <!-- Winners -->
        <div class="flex items-center gap-1 mt-4">
          <WinnerIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Winners
          </BaseText>
        </div>
        <div class="mt-2 flex items-center gap-2">
          <div class="rounded-md bg-background-normal px-2 py-1 flex items-center gap-1">
            <BaseText size="sm" class="text-text-normal">
              is
            </BaseText>
            <BaseText type="label" size="sm" class="text-text-muted">
              Scaling
            </BaseText>
            <BaseText size="sm" class="text-text-normal">
              and...
            </BaseText>
          </div>
          <!-- TODO: @Nate - Allow FP metrics to be selected for winners metric when support is added -->
          <QueryCondition
            :key="`winners-${platformToggle}`"
            class="border-none fade-in"
            style="padding: 0px !important"
            hide-clause
            hide-actions
            restrict-width
            :excluded-categories="['foreplay_metrics', 'tags']"
            :filter-condition="creativeTargetsFilters[platformToggle]?.winners ?? {}"
            filter-option-type="metric"
            :ignored-operator-keys="['equals']"
            :currency-region="currencyRegion"
            :metrics-override="metricOverrides[platformToggle] ?? {}"
            :platform-override="platformToggle"
            @selectProperty="changeConditionProperty('winners', $event)"
            @selectOperation="changeConditionOperation('winners', $event)"
            @updateConditionValue="updateConditionValue('winners', $event)"
          />
        </div>
        <!-- Losers -->
        <div class="flex items-center gap-1 mt-4">
          <SignDirectionIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Losers
          </BaseText>
        </div>
        <div class="mt-2">
          <!--  -->
          <QueryCondition
            :key="`losers-${platformToggle}`"
            class="border-none fade-in"
            style="padding: 0px !important"
            hide-clause
            hide-actions
            :filter-condition="creativeTargetsFilters[platformToggle]?.losers ?? {}"
            filter-option-type="metric"
            :freeze-property="true"
            :freeze-operation="true"
            :currency-region="currencyRegion"
            @selectProperty="changeConditionProperty('losers', $event)"
            @selectOperation="changeConditionOperation('losers', $event)"
            @updateConditionValue="updateConditionValue('losers', $event)"
          />
        </div>
      </div>
      <!-- Footer -->
      <div class="flex items-center justify-between p-5 border-t border-border-normal">
        <button class="p-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="$emit('close')">
          <BaseText type="label" size="sm" class="text-text-muted">
            Cancel
          </BaseText>
        </button>
        <button class="submit-btn black-btn-primary transition-colors text-white bg-black px-3 py-1.5 rounded-md"
        :disabled="loading || !isTargetValid.state" @click="onTargetSave">
          <BaseLoadingSpinnerCircle v-if="loading" />
          <BaseText v-else type="label" size="sm">
            {{ isNewTarget ? 'Create target' : 'Update Targets' }}
          </BaseText>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters } from 'vuex'
import LensAPI from '@/api/lens'
import { providerKeyMap, platformNameMap } from '@/utils/lens/dataSourceUtils'
import { parseFiltersFromQuery, createFilterQuery } from '@/utils/lens/filters/filterQueryUtils'
import { getMatchingPresetName, formatCustomDateRangeLabel } from '@/utils/dateUtils'

// Components
import QueryCondition from './filters/QueryCondition.vue'
import PlatformToggle from './PlatformToggle.vue'

// Icons
import NewLaunchedIcon from '../globals/Icons/NewLaunchedIcon.vue'
import ScaleIcon from '../globals/Icons/ScaleIcon.vue'
import SignDirectionIcon from '../globals/Icons/SignDirectionIcon.vue'
import TargetGoalIcon from '../globals/Icons/TargetGoalIcon.vue'
import WinnerIcon from '../globals/Icons/WinnerIcon.vue'
import AltAlertIcon from '../globals/Icons/AltAlertIcon.vue'

export default {
  name: 'CreativeTargetModal',
  components: {
    QueryCondition,
    PlatformToggle,
    // Icons
    AltAlertIcon,
    NewLaunchedIcon,
    ScaleIcon,
    SignDirectionIcon,
    TargetGoalIcon,
    WinnerIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    isNewTarget: {
      type: Boolean,
      default: false
    },
    rawLighthouseConfig: {
      type: Object,
      default: () => {}
    },
    launchDateRange: {
      type: Object,
      default: () => {}
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    }
  },
  data () {
    const TARGET = {
      LAUNCHED: 'new_launched',
      SCALING: 'scaling',
      WINNERS: 'winners',
      LOSERS: 'losers'
    }
    const LAUNCH_STATS = [
      { name: 'New Launched', key: TARGET.LAUNCHED, value: 0, icon: NewLaunchedIcon },
      { name: 'Scaling', key: TARGET.SCALING, value: 0, icon: ScaleIcon },
      { name: 'Winners', key: TARGET.WINNERS, value: 0, icon: WinnerIcon },
      { name: 'Losers', key: TARGET.LOSERS, value: 0, icon: SignDirectionIcon }
    ]

    return {
      platformToggle: 'meta',
      
      LAUNCH_STATS,
      TARGET,

      loading: false,
      hasMounted: false,

      creativeTargetsFilters: {},
      dateFilterLabel: null
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricGroupsAllPlatforms', 'getIsLoadingMetrics']),
    showPlatformToggle () {
      if (!this.getSidebarLenses?.length) {
        // Return false if sidebar lenses are not loaded yet
        return false
      } else {
        const currentLens = this.getSidebarLenses.find(lens => lens.id === this.lensId)
        // Determine if the lens has more than one provider in its connected ad accounts
        const connectedProviders = new Set(currentLens.accounts.map(account => account.provider))
        return connectedProviders.size > 1
      }
    },
    isTargetValid () {
      Object.values(this.creativeTargetsFilters).forEach(platformFilters => {
        const { scaling, winners, losers } = platformFilters
        const isNotEmpty = scaling.value !== null && winners.value !== null && losers.value !== null
        const isValid = isNotEmpty && (losers.value <= scaling.value)
        const errMessage = !isNotEmpty ? 'All fields are required' : !(losers.value <= scaling.value) ? 'Scaling must be greater than losers' : null
        if (!isValid) return { state: false, message: `(${platformNameMap[this.platformToggle]}) ${errMessage}` }
      }) 
      return { state: true, message: null }
    },
    metricOverrides () {
      if (this.getIsLoadingMetrics) return {}
      return Object.fromEntries(Object.entries(this.getMetricGroupsAllPlatforms).map(([platform, metricGroups]) => [platform, { metricGroups } ]))
    }
  },
  watch: {
    launchDateRange: {
      handler (newVal) {
        if (!newVal?.start || !newVal?.end) {
          this.dateFilterLabel = 'All Time'
        } else if (newVal.isRelativeToToday) {
          this.dateFilterLabel = getMatchingPresetName(newVal) || formatCustomDateRangeLabel(newVal.start, newVal.end)
        } else {
          this.dateFilterLabel = formatCustomDateRangeLabel(newVal.start, newVal.end)
        }
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    this.platformToggle = this.getMetricsPlatform

    const defaultTargetQuery = parseFiltersFromQuery([
      [{ key: 'spend', value: 100, operation: 'greater_than' }], 
      [{ key: 'roas', value: 1, operation: 'greater_than' }], 
      [{ key: 'spend', value: 10, operation: 'less_than' }]]
    )
    const defaultTargetFilters = {
      scaling: defaultTargetQuery[0][0],
      winners: defaultTargetQuery[1][0],
      losers: defaultTargetQuery[2][0]
    }

    if (_.isEmpty(this.rawLighthouseConfig)) {
      const defaultObj = {}
      Object.keys(providerKeyMap).forEach(platform => { defaultObj[platform] = defaultTargetFilters })
      this.creativeTargetsFilters = defaultObj
    } else {
      const parsedObj = {}
      Object.keys(providerKeyMap).forEach(platform => {
        const { winners: winnerConfig, losers: loserConfig, scaling: scaleConfig } = (platform === 'meta' ? this.rawLighthouseConfig : this.rawLighthouseConfig[`${platform}_config`])
        const initTargetFilters = parseFiltersFromQuery([scaleConfig[0], winnerConfig[0], loserConfig[0]])
        parsedObj[platform] = {
          scaling: initTargetFilters[0][0],
          winners: initTargetFilters[1][0],
          losers: initTargetFilters[2][0]
        }
      })
      this.creativeTargetsFilters = parsedObj
    }
    this.hasMounted = true
  },
  methods: {
    async saveCreativeTarget (isUpdate) {
      this.loading = true
      try {
        // Create payload (ORDER MATTERS)
        const { scaling, winners, losers } = this.creativeTargetsFilters.meta
        const payload = {
          scaling: createFilterQuery([[scaling]]),
          winners: createFilterQuery([[winners]]),
          losers: createFilterQuery([[losers]])
        }
        Object.keys(providerKeyMap).filter(platform => platform !== 'meta').forEach(platform => {
          payload[`${platform}_config`] = {
            scaling: createFilterQuery([[this.creativeTargetsFilters[platform].scaling]]),
            winners: createFilterQuery([[this.creativeTargetsFilters[platform].winners]]),
            losers: createFilterQuery([[this.creativeTargetsFilters[platform].losers]])
          }
        })

        // Create & Update
        let newLighthouseConfig
        if (isUpdate) {
          const currentConfigId = this.rawLighthouseConfig.id
          const { data } = await LensAPI.Lens.Lighthouse.updateConfig(this.lensId, currentConfigId, payload)
          newLighthouseConfig = data
        } else {
          const { data } = await LensAPI.Lens.Lighthouse.createConfig(this.lensId, payload)
          newLighthouseConfig = data
        }
        // Emit the updated config for local updates
        this.$emit('save', newLighthouseConfig)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
      } finally {
        this.loading = false
      }
    },
    async onTargetSave () {
      const isUpdate = !this.isNewTarget
      await this.saveCreativeTarget(isUpdate)
    },
    // ============== UI Methods ================== \\
    changeConditionProperty (targetKey, newProperty) {
      const oldCondition = this.creativeTargetsFilters[this.platformToggle][targetKey]
      const newCondition = {
        property: newProperty,
        operation: newProperty?.validOperations?.[0] || null,
        value: null,
        renderKey: oldCondition.renderKey
      }
      this.creativeTargetsFilters[this.platformToggle][targetKey] = newCondition
    },
    changeConditionOperation (targetKey, newOperation) {
      this.creativeTargetsFilters[this.platformToggle][targetKey].operation = newOperation
    },
    updateConditionValue (targetKey, value) {
      this.creativeTargetsFilters[this.platformToggle][targetKey].value = value
    }
  }
}
</script>

<style scoped>
.submit-btn:disabled {
    color: #a4abb8 !important;
    background-color: #F6F8FA !important;
    box-shadow: none;
}
.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0.25; }
  to { opacity: 1; }
}
</style>
